import { useEffect, useMemo, useState } from "react";
import { api, fmtErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { RoleBadge, ROLE_LABELS } from "@/components/badges";
import { ConfirmDialog, ActivationModePicker, copyText } from "@/components/common";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Search, CalendarOff } from "lucide-react";
import { DelegationForm } from "@/components/DelegationForm";

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [createdPwd, setCreatedPwd] = useState(null);
  const [saving, setSaving] = useState(false);
  const isSuper = user?.role === "super_admin";
  const canCreate = isSuper || user?.role === "company_admin";
  const canManage = isSuper || ["company_admin", "manager"].includes(user?.role);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", role: "collaborator", company_id: "", client_id: "",
    activation_mode: "invite", password: "", password_confirm: "", all_categories: false, category_ids: [] });
  const [userType, setUserType] = useState(isSuper ? "internal" : "portal");
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [fCompany, setFCompany] = useState("");
  const [fRole, setFRole] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [disableTarget, setDisableTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [delegationTarget, setDelegationTarget] = useState(null);

  const load = () => {
    api.get("/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    if (isSuper) api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => { if (open && !isSuper) setUserType("portal"); }, [open]);

  useEffect(() => {
    if (!open || userType !== "portal") return;
    const cid = isSuper ? form.company_id : "";
    if (isSuper && !cid) { setClients([]); return; }
    api.get(isSuper ? `/clients?company_id=${cid}` : "/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, [open, userType, form.company_id]);

  const visible = useMemo(() => {
    let list = users || [];
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((u) => `${u.first_name} ${u.last_name}`.toLowerCase().includes(s) || u.email.toLowerCase().includes(s));
    }
    if (fCompany) list = list.filter((u) => u.company_id === fCompany);
    if (fRole) list = list.filter((u) => u.role === fRole);
    if (fStatus) list = list.filter((u) => u.status === fStatus);
    if (fCategory) {
      const cat = categories.find((c) => c.id === fCategory);
      list = list.filter((u) => (!cat || u.company_id === cat.company_id) && (u.all_categories || (u.category_ids || []).includes(fCategory)));
    }
    return list;
  }, [users, search, fCompany, fRole, fStatus, fCategory]);

  const scopedCategories = (companyId) => categories.filter((c) => (isSuper ? c.company_id === companyId : true));

  const CategoryPicker = ({ allCats, catIds, companyId, testid, onChange }) => {
    const cats = scopedCategories(companyId);
    return (
      <div className="space-y-2" data-testid={`${testid}-categories-section`}>
        <Label>Catégories attribuées</Label>
        <label className="flex cursor-pointer items-center gap-2 rounded-sm border border-border p-3 text-sm hover:bg-accent transition-colors">
          <Checkbox data-testid={`${testid}-all-categories`} checked={allCats}
            onCheckedChange={(v) => onChange({ all_categories: !!v, category_ids: v ? [] : catIds })} />
          <span className="font-semibold">Toutes les catégories</span>
          <span className="text-xs text-muted-foreground">— reçoit aussi les futures catégories</span>
        </label>
        {!allCats && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {cats.map((c) => (
              <label key={c.id} data-testid={`${testid}-category-${c.id}`}
                className="flex cursor-pointer items-center gap-2 rounded-sm border border-border p-2.5 text-sm hover:bg-accent transition-colors">
                <Checkbox checked={catIds.includes(c.id)}
                  onCheckedChange={(v) => onChange({ all_categories: false, category_ids: v ? [...catIds, c.id] : catIds.filter((x) => x !== c.id) })} />
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: c.color }} />
                <span className="truncate">{c.name}</span>
              </label>
            ))}
            {cats.length === 0 && <p className="col-span-full text-xs text-muted-foreground">Aucune catégorie pour cette société — créez-en d'abord.</p>}
          </div>
        )}
      </div>
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let data;
      if (userType === "portal") {
        const res = await api.post(`/clients/${form.client_id}/portal-users`, {
          first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone || null,
          activation_mode: form.activation_mode,
          password: form.activation_mode === "manual" ? form.password : null,
          password_confirm: form.activation_mode === "manual" ? form.password_confirm : null,
        });
        data = res.data;
      } else {
        const role = userType === "platform" ? "super_admin" : form.role;
        const res = await api.post("/users", {
          first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone || null,
          role, company_id: userType === "platform" ? null : (isSuper ? form.company_id : null),
          user_type: userType === "platform" ? "staff_suivio" : "internal_client",
          activation_mode: form.activation_mode,
          password: form.activation_mode === "manual" ? form.password : null,
          password_confirm: form.activation_mode === "manual" ? form.password_confirm : null,
          all_categories: userType === "internal" ? form.all_categories : false,
          category_ids: userType === "internal" && !form.all_categories ? form.category_ids : [],
        });
        data = res.data;
      }
      setOpen(false);
      setForm({ first_name: "", last_name: "", email: "", phone: "", role: "collaborator", company_id: "", client_id: "", activation_mode: "invite", password: "", password_confirm: "", all_categories: false, category_ids: [] });
      load();
      if (data.activation_link) setCreatedPwd({ email: data.email, link: data.activation_link, emailStatus: data.email_status });
      const createdMsg = data.email_status === "envoye" ? "Utilisateur créé — invitation envoyée par email (Brevo)"
        : data.email_status === "erreur" ? "Utilisateur créé — échec d'envoi de l'email, lien de secours affiché"
        : form.activation_mode === "invite" ? "Utilisateur créé — invitation simulée (aucun email réel)" : "Utilisateur créé et actif";
      toast.success(createdMsg);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const identityOk = form.first_name && form.last_name && form.email;
  const activationOk = form.activation_mode === "invite" || (form.password.length >= 8 && form.password === form.password_confirm);
  const scopeOk = userType === "platform" ? true
    : userType === "internal" ? (!isSuper || !!form.company_id)
    : (!!form.client_id && (!isSuper || !!form.company_id));
  const canSubmitUser = identityOk && activationOk && scopeOk;

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/users/${editTarget.id}`, {
        first_name: editTarget.first_name, last_name: editTarget.last_name, email: editTarget.email,
        phone: editTarget.phone || null, role: editTarget.role,
        company_id: isSuper ? editTarget.company_id : null,
        all_categories: !!editTarget.all_categories,
        category_ids: editTarget.all_categories ? [] : (editTarget.category_ids || []),
      });
      toast.success("Utilisateur mis à jour");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const doDisable = async () => {
    try {
      await api.patch(`/users/${disableTarget.id}`, { status: disableTarget.status === "active" ? "disabled" : "active" });
      toast.success(disableTarget.status === "active" ? "Compte désactivé" : "Compte réactivé");
      load();
    } catch (e) { toast.error(fmtErr(e)); throw e; }
  };

  const doReset = async () => {
    try {
      const { data } = await api.post(`/users/${resetTarget.id}/reset-password`);
      setCreatedPwd({ email: data.email, link: data.activation_link, emailStatus: data.email_status });
      toast.success(data.email_status === "envoye" ? "Lien généré — email envoyé via Brevo" : "Lien de réinitialisation généré");
    } catch (e) { toast.error(fmtErr(e)); throw e; }
  };

  const resendInvitation = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/resend-invitation`);
      toast.success(data.email_status === "envoye" ? "Invitation renvoyée par email (Brevo)"
        : data.email_status === "erreur" ? "Échec d'envoi — lien de secours affiché"
        : "Invitation renvoyée (email simulé)");
      if (data.activation_link) setCreatedPwd({ email: data.email, link: data.activation_link, emailStatus: data.email_status });
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (e) { toast.error(fmtErr(e)); throw e; }
  };

  return (
    <div data-testid="users-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Utilisateurs</h1>
          <p className="mt-1 text-sm text-muted-foreground">Personnel Suivio360, utilisateurs internes des sociétés clientes et utilisateurs portail client. Choisissez le type à la création.</p>
        </div>
        {canCreate && (
          <Button data-testid="create-user-button" onClick={() => setOpen(true)} className="rounded-sm bg-primary text-white hover:bg-red-700">
            <Plus className="mr-2 h-4 w-4" /> Nouvel utilisateur
          </Button>
        )}
      </div>

      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="search-users" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher nom ou email…" className="w-64 rounded-sm pl-9" />
        </div>
        {isSuper && (
          <Select value={fCompany} onValueChange={(v) => setFCompany(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-user-company" className="w-48 rounded-sm"><SelectValue placeholder="Société : toutes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Société : toutes</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={fRole} onValueChange={(v) => setFRole(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-user-role" className="w-48 rounded-sm"><SelectValue placeholder="Rôle : tous" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Rôle : tous</SelectItem>
            {Object.entries(ROLE_LABELS).filter(([k]) => k !== "client_user" && k !== "system").map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={(v) => setFStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-user-status" className="w-44 rounded-sm"><SelectValue placeholder="Statut : tous" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Statut : tous</SelectItem>
            <SelectItem value="active">Actif</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="disabled">Désactivé</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fCategory} onValueChange={(v) => setFCategory(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-user-category" className="w-56 rounded-sm"><SelectValue placeholder="Catégorie : toutes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Catégorie : toutes</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{isSuper ? `${c.name}${companies.find((co) => co.id === c.company_id) ? ` (${companies.find((co) => co.id === c.company_id).name})` : ""}` : c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-sm border border-border bg-card">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-5 py-3 font-semibold">Nom</th>
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Rôle</th>
              <th className="px-5 py-3 font-semibold">Catégories</th>
              {isSuper && <th className="px-5 py-3 font-semibold">Société</th>}
              <th className="px-5 py-3 font-semibold">Statut</th>
              <th className="px-5 py-3 font-semibold">Absence</th>
              <th className="px-5 py-3 font-semibold">Créé le</th>
              {canManage && <th className="px-5 py-3 font-semibold"></th>}
            </tr>
          </thead>
          <tbody>
            {visible.map((u) => (
              <tr key={u.id} className="border-b border-border last:border-b-0 hover:bg-accent transition-colors duration-200" data-testid={`user-row-${u.email}`}>
                <td className="px-5 py-3 font-medium">{u.first_name} {u.last_name}</td>
                <td className="px-5 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-5 py-3"><RoleBadge role={u.role} /></td>
                <td className="max-w-56 px-5 py-3" data-testid={`user-categories-${u.email}`}>
                  {u.all_categories
                    ? <span className="whitespace-nowrap rounded-sm bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">Toutes les catégories</span>
                    : (u.category_names || []).length
                      ? <span className="text-xs">{u.category_names.join(", ")}</span>
                      : <span className="text-xs text-muted-foreground">Aucune catégorie</span>}
                </td>
                {isSuper && <td className="px-5 py-3">{u.company_name || "Suivio360"}</td>}
                <td className="px-5 py-3">
                  {u.status === "active"
                    ? <span className="whitespace-nowrap rounded-sm bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-900 dark:bg-green-950 dark:text-green-300">Actif</span>
                    : u.status === "pending"
                      ? <span className="whitespace-nowrap rounded-sm bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-950 dark:text-amber-300">En attente</span>
                      : <span className="whitespace-nowrap rounded-sm bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-900 dark:bg-red-950 dark:text-red-300">Désactivé</span>}
                </td>
                <td className="px-5 py-3 text-muted-foreground">{fmtDate(u.created_at)}</td>
                <td className="px-5 py-3" data-testid={`user-absence-${u.email}`}>
                  {u.delegation_active
                    ? <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-sm bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-950 dark:text-amber-300"><CalendarOff className="h-3 w-3" /> Absent · Remplacé par {u.substitute_name}</span>
                    : u.delegation
                      ? <span className="whitespace-nowrap rounded-sm bg-muted px-2 py-0.5 text-xs text-muted-foreground">Absence programmée</span>
                      : <span className="text-xs text-muted-foreground">—</span>}
                </td>
                {canManage && (
                  <td className="px-5 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`user-actions-${u.email}`} className="rounded-sm border border-border p-1.5 text-muted-foreground hover:text-foreground transition-colors">
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-sm">
                        <DropdownMenuItem data-testid={`user-action-delegation-${u.email}`} onClick={() => setDelegationTarget(u)}>Gérer l'absence</DropdownMenuItem>
                        {canCreate && u.id !== user.id && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem data-testid={`user-action-edit-${u.email}`} onClick={() => setEditTarget({ ...u, all_categories: !!u.all_categories, category_ids: u.category_ids || [] })}>Modifier</DropdownMenuItem>
                            <DropdownMenuItem data-testid={`user-action-reset-${u.email}`} onClick={() => setResetTarget(u)}>Réinitialiser le mot de passe</DropdownMenuItem>
                            {u.status === "pending" && (
                              <DropdownMenuItem data-testid={`user-action-resend-${u.email}`} onClick={() => resendInvitation(u)}>Renvoyer l'invitation</DropdownMenuItem>
                            )}
                            <DropdownMenuItem data-testid={`user-action-toggle-${u.email}`} onClick={() => setDisableTarget(u)}>
                              {u.status === "active" ? "Désactiver" : "Réactiver"}
                            </DropdownMenuItem>
                            {isSuper && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem data-testid={`user-action-delete-${u.email}`} onClick={() => setDeleteTarget(u)} className="text-primary">
                                  Supprimer
                                </DropdownMenuItem>
                              </>
                            )}
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {users && visible.length === 0 && <div className="p-10 text-center text-sm text-muted-foreground">Aucun utilisateur avec ces filtres.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Nouvel utilisateur</DialogTitle>
            <DialogDescription>Choisissez le type d'utilisateur, puis renseignez ses informations. Un email d'activation lui sera envoyé (ou simulé selon l'environnement).</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Type d'utilisateur *</Label>
              <Select value={userType} disabled={!canCreate} onValueChange={(v) => { setUserType(v); setForm({ ...form, role: v === "platform" ? "super_admin" : "collaborator", company_id: "", client_id: "", all_categories: false, category_ids: [] }); }}>
                <SelectTrigger data-testid="user-type-select" className="rounded-sm">
                  <span className="truncate">
                    {userType === "platform" ? "Personnel Suivio360 (plateforme)"
                      : userType === "internal" ? "Membre de votre équipe"
                      : "Utilisateur portail client"}
                  </span>
                </SelectTrigger>
                <SelectContent className="max-w-[--radix-select-trigger-width]">
                  {isSuper && (
                    <SelectItem value="platform">
                      <span className="flex flex-col gap-0.5 py-0.5">
                        <span className="font-medium">Personnel Suivio360 (plateforme)</span>
                        <span className="text-xs text-muted-foreground whitespace-normal">Membre de l'équipe Suivio360 gérant la plateforme globale.</span>
                      </span>
                    </SelectItem>
                  )}
                  {canCreate && (
                    <SelectItem value="internal">
                      <span className="flex flex-col gap-0.5 py-0.5">
                        <span className="font-medium">Membre de votre équipe</span>
                        <span className="text-xs text-muted-foreground whitespace-normal">Collaborateur de votre équipe qui traite et répond aux tickets.</span>
                      </span>
                    </SelectItem>
                  )}
                  <SelectItem value="portal">
                    <span className="flex flex-col gap-0.5 py-0.5">
                      <span className="font-medium">Utilisateur portail client</span>
                      <span className="text-xs text-muted-foreground whitespace-normal">Client final qui utilise le portail pour envoyer ses demandes.</span>
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground" data-testid="user-type-hint">
                {userType === "platform" ? "Membre de l'équipe Suivio360 gérant la plateforme globale."
                  : userType === "internal" ? "Collaborateur de votre équipe qui traite et répond aux tickets."
                  : "Client final qui utilise le portail pour envoyer ses demandes."}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input data-testid="user-first-name-input" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input data-testid="user-last-name-input" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input data-testid="user-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input data-testid="user-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" />
              </div>
            </div>

            {userType === "platform" && (
              <div className="space-y-2">
                <Label>Rôle</Label>
                <div className="rounded-sm border border-border bg-muted px-3 py-2 text-sm font-medium" data-testid="platform-role-fixed">Super Admin — accès plateforme complet</div>
              </div>
            )}

            {userType === "internal" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rôle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {isSuper && <SelectItem value="company_admin">Admin Société</SelectItem>}
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="collaborator">Collaborateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isSuper && (
                  <div className="space-y-2">
                    <Label>Société *</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v, category_ids: [], all_categories: false })}>
                      <SelectTrigger data-testid="user-company-select" className="rounded-sm"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                      <SelectContent>
                        {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {userType === "portal" && (
              <div className="grid grid-cols-2 gap-4">
                {isSuper && (
                  <div className="space-y-2">
                    <Label>Société *</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v, client_id: "" })}>
                      <SelectTrigger data-testid="portal-user-company-select" className="rounded-sm"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                      <SelectContent>
                        {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Client entreprise *</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })} disabled={isSuper && !form.company_id}>
                    <SelectTrigger data-testid="portal-client-select" className="rounded-sm"><SelectValue placeholder={isSuper && !form.company_id ? "Choisir une société d'abord" : "Choisir…"} /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      {clients.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Aucun client entreprise disponible.</div>}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {userType === "internal" && ["manager", "collaborator"].includes(form.role) && (
              <CategoryPicker testid="create-user"
                allCats={form.all_categories} catIds={form.category_ids} companyId={form.company_id}
                onChange={(v) => setForm({ ...form, ...v })} />
            )}

            <ActivationModePicker testid="user-activation"
              mode={form.activation_mode} onModeChange={(v) => setForm({ ...form, activation_mode: v })}
              password={form.password} onPasswordChange={(v) => setForm({ ...form, password: v })}
              confirm={form.password_confirm} onConfirmChange={(v) => setForm({ ...form, password_confirm: v })} />
            <DialogFooter>
              <Button data-testid="user-submit-button" type="submit" disabled={saving || !canSubmitUser} className="rounded-sm bg-primary text-white hover:bg-red-700">
                {saving ? "Création…" : "Créer l'utilisateur"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input data-testid="edit-user-first-name" required value={editTarget.first_name} onChange={(e) => setEditTarget({ ...editTarget, first_name: e.target.value })} className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input data-testid="edit-user-last-name" required value={editTarget.last_name} onChange={(e) => setEditTarget({ ...editTarget, last_name: e.target.value })} className="rounded-sm" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input data-testid="edit-user-email" type="email" required value={editTarget.email} onChange={(e) => setEditTarget({ ...editTarget, email: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input data-testid="edit-user-phone" value={editTarget.phone || ""} onChange={(e) => setEditTarget({ ...editTarget, phone: e.target.value })} className="rounded-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rôle</Label>
                  <Select value={editTarget.role} onValueChange={(v) => setEditTarget({ ...editTarget, role: v })}>
                    <SelectTrigger data-testid="edit-user-role" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company_admin">Admin Société</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="collaborator">Collaborateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isSuper && (
                  <div className="space-y-2">
                    <Label>Société</Label>
                    <Select value={editTarget.company_id || ""} onValueChange={(v) => setEditTarget({ ...editTarget, company_id: v })}>
                      <SelectTrigger data-testid="edit-user-company" className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <CategoryPicker testid="edit-user"
                allCats={!!editTarget.all_categories} catIds={editTarget.category_ids || []} companyId={editTarget.company_id}
                onChange={(v) => setEditTarget({ ...editTarget, ...v })} />
              <DialogFooter>
                <Button data-testid="edit-user-submit" type="submit" disabled={saving} className="rounded-sm bg-primary text-white hover:bg-red-700">
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!createdPwd} onOpenChange={() => setCreatedPwd(null)}>
        <DialogContent className="rounded-sm" data-testid="user-created-dialog">
          <DialogHeader>
            <DialogTitle>{createdPwd?.emailStatus === "erreur" ? "Compte créé, mais email non envoyé" : createdPwd?.emailStatus === "envoye" ? "Invitation envoyée par email" : "Compte créé — invitation simulée"}</DialogTitle>
            <DialogDescription data-testid="invitation-result-message">
              {createdPwd?.emailStatus === "envoye" && "Le compte a été créé et un email d'activation a été envoyé à l'utilisateur via Brevo. Le lien reste disponible ci-dessous en cas de besoin."}
              {createdPwd?.emailStatus === "erreur" && "Le compte a été créé, mais l'email d'invitation n'a pas pu être envoyé. Vous pouvez copier le lien ci-dessous ou réessayer l'envoi depuis les logs email."}
              {(!createdPwd?.emailStatus || createdPwd?.emailStatus === "simule_envoye") && "Le compte a été créé. L'email d'invitation est simulé dans cet environnement (aucun envoi réel) — transmettez le lien ci-dessous à l'utilisateur."}
            </DialogDescription>
          </DialogHeader>
          {createdPwd && (
            <div className="overflow-hidden rounded-sm border border-border p-4 text-sm">
              <div>{createdPwd.email}</div>
              <div className="mt-2 flex items-center gap-2">
                <code data-testid="created-activation-link" className="min-w-0 flex-1 truncate rounded-sm bg-muted px-2 py-1 text-xs">{createdPwd.link}</code>
                <Button size="sm" variant="outline" data-testid="copy-activation-link" className="shrink-0 rounded-sm"
                  onClick={() => copyText(createdPwd.link)}>Copier</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!disableTarget} onOpenChange={() => setDisableTarget(null)}
        title={disableTarget?.status === "active" ? `Désactiver ${disableTarget?.first_name} ${disableTarget?.last_name} ?` : `Réactiver ${disableTarget?.first_name} ${disableTarget?.last_name} ?`}
        description={disableTarget?.status === "active" ? "Êtes-vous sûr de vouloir désactiver cet utilisateur ? Il ne pourra plus se connecter. Action journalisée." : "L'utilisateur pourra de nouveau se connecter."}
        confirmLabel={disableTarget?.status === "active" ? "Désactiver" : "Réactiver"}
        onConfirm={doDisable} testid="disable-user-dialog" />

      <ConfirmDialog open={!!resetTarget} onOpenChange={() => setResetTarget(null)}
        title={`Réinitialiser le mot de passe de ${resetTarget?.email} ?`}
        description="Un nouveau lien d'activation (valide 72 h) sera généré : l'utilisateur devra définir un nouveau mot de passe. Un email avec ce lien lui sera envoyé (ou simulé selon la configuration de l'environnement)."
        confirmLabel="Réinitialiser" onConfirm={doReset} testid="reset-password-dialog" />

      <ConfirmDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}
        title={`Supprimer définitivement ${deleteTarget?.email} ?`}
        description="Action irréversible réservée au Super Admin. Pour conserver l'historique, préférez la désactivation. Action journalisée."
        confirmLabel="Supprimer définitivement" onConfirm={doDelete} testid="delete-user-dialog" />

      <Dialog open={!!delegationTarget} onOpenChange={() => setDelegationTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="delegation-dialog">
          <DialogHeader>
            <DialogTitle>Absence de {delegationTarget?.first_name} {delegationTarget?.last_name}</DialogTitle>
            <DialogDescription>
              Pendant la période définie, les nouveaux tickets attribués à cette personne seront automatiquement redirigés vers le remplaçant, puis reviendront à la date de fin.
            </DialogDescription>
          </DialogHeader>
          {delegationTarget && (
            <DelegationForm userId={delegationTarget.id} onChanged={load} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
